import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { 
  Package, 
  IndianRupee, 
  Layers, 
  RefreshCw, 
  Search,
  ArrowDownCircle,
  ArrowUpCircle,
  Download,
  Clock,
  ChevronsUpDown,
  Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BatchStockLedgerPage() {
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch all items for dropdown
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/items`, {
          headers: getAuthHeader()
        });
        setItems(response.data);
      } catch (error) {
        toast.error('Failed to load items');
      } finally {
        setItemsLoading(false);
      }
    };
    fetchItems();
  }, []);

  // Fetch ledger when item is selected
  const fetchLedger = async (itemId) => {
    if (!itemId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/inventory-batches/${itemId}/ledger`, {
        headers: getAuthHeader()
      });
      setLedgerData(response.data);
    } catch (error) {
      toast.error('Failed to load batch ledger');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (itemId) => {
    setSelectedItemId(itemId);
    setOpen(false);
    fetchLedger(itemId);
  };

  // Filter and sort items for dropdown based on search
  const filteredItems = useMemo(() => {
    let result = [...items];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.name?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term)
      );
    }
    
    // Sort alphabetically by name
    result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
    return result;
  }, [items, searchTerm]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    if (!ledgerData?.ledger?.length) return;

    const headers = ['Date', 'Type', 'Source', 'Reference', 'Qty', 'Unit Price', 'Value', 'Balance', 'Description'];
    const rows = ledgerData.ledger.map(entry => [
      formatDateTime(entry.date),
      entry.type,
      entry.source_type,
      entry.source_ref,
      entry.quantity,
      entry.unit_price,
      entry.value,
      entry.balance_quantity,
      entry.description
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch_ledger_${ledgerData.item.code}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Ledger exported successfully');
  };

  const data = ledgerData;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Layers className="w-6 h-6 text-primary" />
              Batch Stock Ledger
            </h1>
            <p className="text-muted-foreground mt-1">
              Complete movement history per batch - when created, consumed, and by which JCs/OJCs
            </p>
          </div>
        </div>

        {/* Item Selector */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Select Item</label>
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      className="w-full justify-between font-normal"
                      data-testid="item-selector-ledger"
                    >
                      {selectedItemId ? (
                        <span className="truncate">
                          {items.find(item => item.id === selectedItemId)?.name || 'Select item...'}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {itemsLoading ? "Loading items..." : "Search and select an item..."}
                        </span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Type to search items..." 
                        value={searchTerm}
                        onValueChange={setSearchTerm}
                      />
                      <CommandList>
                        <CommandEmpty>No item found.</CommandEmpty>
                        <CommandGroup>
                          {filteredItems.map(item => (
                            <CommandItem
                              key={item.id}
                              value={item.id}
                              onSelect={() => handleItemSelect(item.id)}
                              className="cursor-pointer"
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${selectedItemId === item.id ? "opacity-100" : "opacity-0"}`}
                              />
                              <span className="font-medium">{item.name}</span>
                              <span className="text-muted-foreground ml-2 truncate">- {item.description}</span>
                              <Badge variant="outline" className="ml-auto text-xs">{item.category}</Badge>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex gap-2 self-end">
                {selectedItemId && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => fetchLedger(selectedItemId)}
                      disabled={loading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    {data?.ledger?.length > 0 && (
                      <Button variant="outline" onClick={exportToCSV}>
                        <Download className="w-4 h-4 mr-1" />
                        Export CSV
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading batch ledger...</span>
          </div>
        )}

        {/* No Item Selected */}
        {!selectedItemId && !loading && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Search className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Select an item from the dropdown to view its batch ledger</p>
            </CardContent>
          </Card>
        )}

        {/* Ledger Data Display */}
        {data && !loading && (
          <>
            {/* Item Info Header */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-xl font-bold">{data.item.code}</h2>
                    <p className="text-muted-foreground">{data.item.name}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge>{data.item.category}</Badge>
                      <Badge variant="outline">{data.item.unit}</Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Current Balance</p>
                    <p className="text-3xl font-bold text-primary">{data.summary?.current_balance}</p>
                    <p className="text-sm text-muted-foreground">{formatCurrency(data.summary?.current_value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Batches</p>
                      <p className="text-2xl font-bold">{data.summary?.total_batches}</p>
                    </div>
                    <Layers className="w-8 h-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Qty In</p>
                      <p className="text-2xl font-bold text-green-600">+{data.summary?.total_qty_in}</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(data.summary?.total_value_in)}</p>
                    </div>
                    <ArrowDownCircle className="w-8 h-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Qty Out</p>
                      <p className="text-2xl font-bold text-red-600">-{data.summary?.total_qty_out}</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(data.summary?.total_value_out)}</p>
                    </div>
                    <ArrowUpCircle className="w-8 h-8 text-red-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Consumptions</p>
                      <p className="text-2xl font-bold">{data.summary?.total_consumptions}</p>
                    </div>
                    <Package className="w-8 h-8 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Batch Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Active Batches</CardTitle>
                <CardDescription>FIFO order - oldest batches consumed first</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Reference No.</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Consumed</TableHead>
                        <TableHead className="text-right">Remaining</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.batches_detail?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No batches found
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.batches_detail?.map((batch, idx) => (
                          <TableRow key={batch.id || idx}>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDateTime(batch.batch_date)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                batch.source_type === 'opening_stock' ? 'default' :
                                batch.source_type === 'purchase' ? 'secondary' :
                                batch.source_type === 'production' ? 'outline' :
                                'destructive'
                              }>
                                {batch.source_type === 'opening_stock' ? 'Opening' :
                                 batch.source_type === 'purchase' ? 'Purchase' :
                                 batch.source_type === 'production' ? 'Production' :
                                 batch.source_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium font-mono">{batch.source_ref || '-'}</TableCell>
                            <TableCell className="text-right">{batch.qty_received}</TableCell>
                            <TableCell className="text-right text-red-600">{batch.qty_consumed}</TableCell>
                            <TableCell className="text-right font-bold text-green-600">{batch.qty_remaining}</TableCell>
                            <TableCell className="text-right">{formatCurrency(batch.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(batch.qty_remaining * batch.unit_price)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Ledger Entries */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Stock Ledger</CardTitle>
                <CardDescription>Complete movement history with running balance</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Reference No.</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.ledger?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No ledger entries found
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.ledger?.map((entry, idx) => (
                          <TableRow key={idx} className={entry.type === 'IN' ? 'bg-green-50/30' : 'bg-red-50/30'}>
                            <TableCell className="text-sm">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-muted-foreground" />
                                {formatDateTime(entry.date)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={entry.type === 'IN' ? 'default' : 'destructive'}>
                                {entry.type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {entry.source_type === 'opening_stock' ? 'Opening' :
                                 entry.source_type === 'purchase' ? 'Purchase' :
                                 entry.source_type === 'production' ? 'Production' :
                                 entry.source_type === 'jc_issue' ? 'JC Issue' :
                                 entry.source_type === 'ojc_issue' ? 'OJC Issue' :
                                 entry.source_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium font-mono max-w-[150px] truncate" title={entry.source_ref}>
                              {entry.source_ref || '-'}
                            </TableCell>
                            <TableCell className={`text-right font-bold ${entry.type === 'IN' ? 'text-green-600' : 'text-red-600'}`}>
                              {entry.type === 'IN' ? '+' : ''}{entry.quantity}
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                            <TableCell className={`text-right ${entry.type === 'IN' ? 'text-green-600' : 'text-red-600'}`}>
                              {entry.type === 'IN' ? '+' : ''}{formatCurrency(entry.value)}
                            </TableCell>
                            <TableCell className="text-right font-bold">{entry.balance_quantity}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
